import React, { useState } from 'react';
import './BarChart.css';
import { prettySecondsToTime } from '../utils/functions';
import moment from 'moment';
import 'moment/locale/sv';
import { useTranslation } from '../i18n';

const BarChart = ({ sessions }) => {
  const lang = localStorage.getItem('language');
  const { t } = useTranslation();
  const [maxSizePages, setMaxHeightPages] = useState(0);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [currentIndex, setCurrentIndex] = useState(null);

  const showTooltip = () => setVisible(true);
  const hideTooltip = () => setVisible(false);

  const handleMouseMove = (index) => (e) => {
    setPosition({
      top: e.nativeEvent.clientY,
      left: e.nativeEvent.clientX,
    });
    setCurrentIndex(index);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0]; // Get the first touch point
    const bars = document.querySelectorAll('.bar-wrapper');
    let newIndex = null;

    // Loop through all bars to find which bar the touch is currently over
    bars.forEach((bar, index) => {
      const rect = bar.getBoundingClientRect();
      if (
        touch.clientX >= rect.left &&
        touch.clientX <= rect.right &&
        touch.clientY >= rect.top &&
        touch.clientY <= rect.bottom
      ) {
        newIndex = index;
      }
    });

    if (newIndex !== null) {
      setPosition({
        top: touch.clientY,
        left: touch.clientX,
      });
      setCurrentIndex(newIndex);
    }
  };

  const handleTouchStart = (index) => (e) => {
    const touch = e.touches[0]; // Get the first touch point
    setPosition({
      top: touch.clientY,
      left: touch.clientX,
    });
    setCurrentIndex(index);
    showTooltip();
  };

  const handleTouchEnd = () => {
    setCurrentIndex(null);
    hideTooltip();
  };

  const daysBetweenDates = (date1, date2) => {
    const startMoment = moment(date1).startOf('day');
    const endMoment = moment(date2).startOf('day');

    return endMoment.diff(startMoment, 'days');
  };

  React.useEffect(() => {
    // Determine the maximum pages for bar height scaling
    const maxPages = Math.max(...sessions.map((session) => session.pages));
    setMaxHeightPages(maxPages);
  }, [sessions]);

  let indexOffset = 0;
  return (
    <div className="chart-wrapper">
      <div className="bar-chart">
        {sessions
          .sort((a, b) => a.date - b.date)
          .map((session, index) => {
            let dayDiff = 0;
            let daysEmpty = [];
            if (index > 0) {
              dayDiff = daysBetweenDates(
                sessions[index - 1].date.toDate(),
                session.date.toDate(),
              );
              if (dayDiff > 1) {
                daysEmpty.push(
                  <div
                    className="bar-wrapper"
                    key={`empty-${index}`}
                    onMouseEnter={showTooltip}
                    onMouseLeave={handleTouchEnd}
                    onMouseMove={handleMouseMove(index + indexOffset)}
                    onTouchStart={handleTouchStart(index + indexOffset)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div className="bar-y">
                      <div
                        className={`bar ${dayDiff > 2 ? 'bars-empty' : 'bar-empty'}`}
                      ></div>
                    </div>
                    {visible && index + indexOffset === currentIndex && (
                      <div
                        className="bar-tooltip"
                        style={{
                          top: `${position.top}px`,
                          left: `${position.left}px`,
                        }}
                      >
                        <div className="pt-65">
                          {dayDiff > 2 &&
                            moment(session.date.toDate())
                              .subtract(dayDiff - 1, 'days')
                              .locale(lang)
                              .format('D MMM')}
                          {dayDiff > 2 && '—'}
                          {moment(session.date.toDate())
                            .subtract(1, 'days')
                            .locale(lang)
                            .format('D MMM, YYYY')}
                        </div>
                        <div>{t.nosession}</div>
                      </div>
                    )}
                  </div>,
                );
                indexOffset++;
              }
            }

            return [
              daysEmpty,
              <div
                className={`bar-wrapper ${index + indexOffset === currentIndex ? 'hover' : ''}`}
                key={session.date}
                onMouseEnter={showTooltip}
                onMouseLeave={handleTouchEnd}
                onMouseMove={handleMouseMove(index + indexOffset)}
                onTouchStart={handleTouchStart(index + indexOffset)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div className="bar-y">
                  <div
                    className="bar"
                    style={{
                      height: (100 * session.pages) / maxSizePages + '%',
                    }}
                  ></div>
                </div>

                {visible && index + indexOffset === currentIndex && (
                  <div
                    className="bar-tooltip"
                    style={{
                      top: `${position.top}px`,
                      left: `${position.left}px`,
                    }}
                  >
                    <div className="pt-65">
                      {moment(session.date.toDate())
                        .locale(lang)
                        .format('D MMM, YYYY')}
                    </div>
                    <div>{`${session.pages} ${t.pagesLow}`}</div>
                    <div>{prettySecondsToTime(session.duration)}</div>
                  </div>
                )}
              </div>,
            ];
          })}
      </div>
    </div>
  );
};

export default BarChart;
